import type { FirestoreTimestamp } from '../types/firestore';
import { PaymentSuppliers } from '../db-models/payments';
import { StoreCategory } from '../../entities/platform-entities/store/enums/store-category.enum';
import type { StoreCustomCss } from '../../entities/platform-entities/store/interfaces/store-custom-css.interface';
import type { StoreFeatureFlags } from '../../entities/platform-entities/store/interfaces/store-feature-flags.interface';

export type { StoreFeatureFlags };

export type BroadcastResolution = '1080p30fps' | '1080p60fps' | '720p30fps' | '720p60fps';

export enum StoreType {
  DEMO = 'Demo',
  TEST = 'Test',
  LIVE = 'Live',
}

export enum VoteResultsMode {
  AfterVote = 'after-vote',
  Always = 'always',
}

export class DbStoreModel {
  id: string;
  name: string;
  deleted?: boolean = false;
  location: string;
  description: string;
  createDate: FirestoreTimestamp;
  title: string;
  url: string;
  externalUrl: string | undefined;
  checkoutRedirectRoute: string | undefined;
  logoUrl: string;
  productsLogoUrl: string;
  emailsLogo: string;
  bannerUrl: string;
  mobileBannerUrl: string;
  measurementsUrl: string;
  taxName?: string;
  taxPercent?: number;
  currency: string;
  industry: string;
  paymentSupplier: PaymentSuppliers;
  priceFormat: string | undefined;
  isPublished: boolean;
  pictureInPicture: boolean;
  theme?: CustomTheme;
  reminderOptions?: ReminderOptions;
  totalSiteVisitors?: number;
  stripe?:
    | {
        publicKey?: string;
        secretKeyFirstChars?: string;
        stripeAccountId?: string;
      }
    | undefined;
  maximumTimelineVideoLength?: number;
  broadcastResolution: BroadcastResolution;
  lastScheduledSyncTime?: number;
  lastSyncTime?: number;
  updatedAt?: Date;
  updatedBy?: string;
  createdAt?: Date;
  createdBy?: string;
  isAutoCheckoutEnabled?: boolean;
  storeConfig?: {
    embed?: {
      hoursBeforeSessionToEmbed?: number;
      embedPostSession?: boolean;
    };
  };
  featureFlags?: StoreFeatureFlags;
  hostVideoGrid?: boolean;
  storeType?: StoreType;
  customLoaderVideoURL?: string;
  externalCart?: boolean;
  apiToken?: string;
  externalApiUrl?: string;
  defaultLanguage?: string;
  defaultDateFormat?: string;
  allowedDomains?: StoreAllowedDomain[];
  colorPalateSelector?: boolean;
  shopifyApp?: {
    subscriptionPlanId?: string;
    billingEndDate?: string;
    billingStartDate?: string;
    fullyLimited?: boolean;
    partiallyLimited?: boolean;
  };
  socialImportsMaxOnDemandRequestsDaily?: number;
  isActiveEmbedSessionBtnExpand?: boolean;
  isEmbedSessionBtnExpandClosedOnStart?: boolean;
  customCss?: StoreCustomCss;
  hideTerrificCart?: boolean;
  cartQuantityControlsSupported?: boolean = true;
  storeMemberPriceDiscount?: number; // Discount percentage for store member prices
  carouselsSettings?: {
    horizontalViewCustomHeadContent?: string;
    verticalViewCustomHeadContent?: string;
  };
  storeCategory?: StoreCategory;
}

export class StoreAllowedDomain {
  id: string;
  addressPattern: string;
  isEnabled: boolean;
  createdAt: FirestoreTimestamp;
  createdBy: string;
  updatedAt: FirestoreTimestamp;
  updatedBy: string;
}

// stores/{storeId}/managers/{userId}
export class DbStoreManagerModel {
  userId: string;
  email: string;
  role: string;
  addDate: FirestoreTimestamp;
  addedByUser: string;
}

// storeUserInvites/{id}
export class DbStoreUserInviteModel {
  id?: string;
  storeId: string;
  email: string;
  active: boolean;
  addedByUserId: string;
  inviteDate: FirestoreTimestamp;
}

export class DbStoreShippingMethodModel {
  id: string;
  storeId: string;
  name: string;
  price?: number;
}

export interface CustomTheme {
  primaryColor?: string;
  secondaryColor?: string;
  lightColor?: string;
}

export interface CoriunderAppKeys {
  merchantnumber: string;
  personalhashkey: string;
}

export class StoreCredential {
  token: string;
  externalUrl: string;
}

export interface ReminderOptions {
  phone: boolean;
  email: boolean;
}

export interface SendReminderData {
  name?: string;
  email?: string;
  phone?: string;
  consents?: string[];
}
